import firebase from "firebase/compat/app";
import { db } from "services/firebase";
import { cachedRead } from "services/usageCache";

/**
 * Per-organization ChartMind activity, read from the counters the org already owns:
 *
 *   organizations/{orgId}/chartmind_daily/{dateKey}     step arrivals per day
 *   organizations/{orgId}/chartmind_user_stats/{uid}    step arrivals per user per month
 *
 * Both are already readable by org admins under existing rules, so this needs no callable,
 * no rules change and no extra writes. It deliberately does NOT read the platform-wide
 * llm_usage_daily rollups: those carry every organization's spend in one document, and an
 * org-facing page has no business being able to fetch it.
 *
 * WHY STEP COUNTERS AND NOT SESSION DOCS: chartmind_daily is written on every step arrival
 * regardless of whether the session persists, so it counts Kijabe beta and simulation
 * encounters too. chartmind_usage_daily counts only persisted completions — in prod that is
 * 16 encounters against 299 real ones.
 */

const STEP_STARTED = "record";
const STEP_CHARTED = "chart";

/**
 * Local date key, matching what ChartMindPage writes.
 *
 * Deliberately not UTC: the writer uses local date parts, so a UTC key here would miss the
 * doc entirely for anyone west of Greenwich late in the day. (The platform rollups use UTC;
 * these two collections are written by different code and do not share a convention.)
 */
export const toLocalDateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export const localDateKeysForRange = (days) => {
  const keys = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    keys.push(toLocalDateKey(d));
  }
  return keys;
};

export const currentYearMonth = () => toLocalDateKey(new Date()).slice(0, 7);

/**
 * Daily encounter activity for the last `days` days.
 *
 * One document per day, so a 90-day window is at most 90 reads however busy the clinic is —
 * and cached, so leaving the page and coming back is zero reads rather than another 90.
 */
export function fetchOrgDailyActivity(organizationId, days) {
  if (!organizationId) return Promise.resolve({ dailyRows: [], totals: null });
  return cachedRead(
    `orgDaily:${organizationId}:${days}:${toLocalDateKey(new Date())}`,
    () => readOrgDailyActivity(organizationId, days),
  );
}

async function readOrgDailyActivity(organizationId, days) {
  const keys = localDateKeysForRange(days);
  const snap = await db
    .collection("organizations")
    .doc(organizationId)
    .collection("chartmind_daily")
    .where(firebase.firestore.FieldPath.documentId(), ">=", keys[0])
    .where(firebase.firestore.FieldPath.documentId(), "<=", keys[keys.length - 1])
    .get();

  const byKey = {};
  snap.forEach((doc) => {
    byKey[doc.id] = doc.data()?.steps || {};
  });

  const dailyRows = keys.map((dateKey) => {
    const steps = byKey[dateKey] || {};
    return {
      dateKey,
      steps,
      started: Number(steps[STEP_STARTED] ?? 0),
      charted: Number(steps[STEP_CHARTED] ?? 0),
    };
  });

  const totals = dailyRows.reduce(
    (acc, row) => {
      acc.started += row.started;
      acc.charted += row.charted;
      Object.entries(row.steps).forEach(([step, n]) => {
        acc.steps[step] = (acc.steps[step] || 0) + Number(n || 0);
      });
      return acc;
    },
    { started: 0, charted: 0, steps: {} },
  );

  return { dailyRows, totals, docsRead: snap.size };
}

/**
 * Per-clinician activity for one month.
 *
 * Month granularity is what chartmind_user_stats stores — it is not a choice made here, and
 * the UI says "this month" rather than implying it matches the daily range above.
 */
export function fetchOrgUserActivity(organizationId, yearMonth) {
  if (!organizationId) return Promise.resolve([]);
  return cachedRead(
    `orgUsers:${organizationId}:${yearMonth}`,
    () => readOrgUserActivity(organizationId, yearMonth),
  );
}

async function readOrgUserActivity(organizationId, yearMonth) {
  const snap = await db
    .collection("organizations")
    .doc(organizationId)
    .collection("chartmind_user_stats")
    .get();

  return snap.docs
    .map((doc) => {
      const data = doc.data() || {};
      const steps = data.months?.[yearMonth] || {};
      return {
        userId: data.userId || doc.id,
        steps,
        started: Number(steps[STEP_STARTED] ?? 0),
        charted: Number(steps[STEP_CHARTED] ?? 0),
      };
    })
    // Someone who did nothing this month is not an "active" clinician. Registered-but-idle
    // accounts inflate every per-user figure, so they are dropped rather than shown as zeros.
    .filter((row) => row.started > 0 || row.charted > 0)
    .sort((a, b) => b.started - a.started);
}

/** Share of started encounters that reached a finished chart. */
export function completionRate(totals) {
  if (!totals || !totals.started) return null;
  return totals.charted / totals.started;
}
